import { useState } from "#imports";
import { useVoixBroadcast } from "@voix/composables/useVoixBroadcast";
import { defineStore } from "pinia";
import queryString from "query-string";
import { v4 as uuidv4 } from "uuid";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useVoixStudioStore } from "./voixStudioStore.js";
export const usePageStore = defineStore("pageStore", () => {
  const broadcast = useVoixBroadcast();
  const currentPath = ref("");
  const currentDomain = ref("");
  const currentPageClean = ref(null);
  const currentPage = ref(null);
  const pageResponse = ref({});
  const hasSetupBroadcast = ref(false);
  const isSaving = ref(false);
  const route = useRoute();
  const query = route.query;
  const inStudioIframe = Object.prototype.hasOwnProperty.call(query, "voix-sync");
  const voixStudioStore = useVoixStudioStore();
  function shareBroadcast(action, data) {
    if (typeof window !== "undefined") {
      const urlParams = queryString.parse(window.location.search);
      let currentAdminId = voixStudioStore.currentAdminId;
      if (urlParams["voix-sync"] !== void 0) {
        currentAdminId = urlParams["voix-sync"];
      }
      const key = `${action}:${currentAdminId}`;
      broadcast.share(key, data);
    }
  }
  function resetAllIds(element) {
    if (element.id)
      element.id = uuidv4();
    if (element.fields) {
      for (const field of element.fields) {
        field.id = uuidv4();
        if (field.media && field.media.length) {
          for (const media of field.media)
            media.id = uuidv4();
        }
      }
    }
    if (element.elements) {
      for (const e of element.elements)
        resetAllIds(e);
    }
  }
  function setIsSaving(value, shouldBroadcast = true) {
    isSaving.value = value;
    if (shouldBroadcast) {
      shareBroadcast("setIsSaving", { value });
    }
  }
  function setCurrentPage(page, shouldBroadcast = true) {
    if (!inStudioIframe || !shouldBroadcast && inStudioIframe) {
      let thePage = JSON.parse(JSON.stringify(page));
      thePage = checkAndSetupElements(thePage);
      thePage = setupForAdministration(thePage);
      currentPageClean.value = JSON.parse(JSON.stringify(thePage));
      currentPage.value = thePage;
    }
    setUpBroadcastReceiversIfNecessary();
    if (shouldBroadcast) {
      if (inStudioIframe)
        shareBroadcast("awaitingCurrentPage", {});
      else
        shareBroadcast("setCurrentPage", { page });
    }
  }
  function sendCurrentPageToIframes() {
    shareBroadcast(`setCurrentPage`, { page: currentPage.value });
  }
  function unsetCurrentPage() {
    currentPage.value = null;
  }
  function checkAndSetupElements(page) {
    if (page) {
      for (const element of page.elements) {
        const elementIndex = page.elements.indexOf(element);
        if (elementIndex) {
          if (!page.elements[elementIndex].properties)
            page.elements[elementIndex].properties = { dom_id: "", settings: [] };
        }
      }
    }
    return page;
  }
  function setCurrentState(stateName, value, shouldBroadcast = true) {
    const state = useState(stateName, () => null);
    state.value = value;
    if (shouldBroadcast)
      shareBroadcast("setCurrentState", { stateName, value });
  }
  function setUpBroadcastReceiversIfNecessary() {
    if (!hasSetupBroadcast.value && typeof window !== "undefined") {
      hasSetupBroadcast.value = true;
      const urlParams = queryString.parse(window.location.search);
      const inStudio = route.path === "/voix/studio";
      if (urlParams["voix-sync"] !== void 0) {
        const currentAdminId = urlParams["voix-sync"];
        broadcast.receive(`setCurrentPage:${currentAdminId}`, (data) => {
          setCurrentPage(data.page, false);
        });
        broadcast.receive(`addElement:${currentAdminId}`, (data) => {
          addElement(data.element, data.destinationId, false);
        });
        broadcast.receive(`duplicateElement:${currentAdminId}`, (data) => {
          duplicateElement(data.elementId, data.newElement, false);
        });
        broadcast.receive(`enableElement:${currentAdminId}`, (data) => {
          enableElement(data.elementId, false);
        });
        broadcast.receive(`disableElement:${currentAdminId}`, (data) => {
          disableElement(data.elementId, false);
        });
        broadcast.receive(`deleteElement:${currentAdminId}`, (data) => {
          deleteElement(data.elementId, false);
        });
        broadcast.receive(`moveElement:${currentAdminId}`, (data) => {
          moveElement(data.elementId, data.destinationId, false);
        });
        broadcast.receive(`setSlot:${currentAdminId}`, (data) => {
          setSlot(data.elementId, data.slot, false);
        });
        broadcast.receive(`moveElementIntoElement:${currentAdminId}`, (data) => {
          moveElementIntoElement(data.elementId, data.destinationId, false);
        });
        broadcast.receive(`fieldUpdate:${currentAdminId}`, (data) => {
          updateField(data.sliceId, data.fieldName, data.value, false);
        });
        broadcast.receive(`addPageField:${currentAdminId}`, (data) => {
          addPageField(data.value, false);
        });
        broadcast.receive(`updatePageField:${currentAdminId}`, (data) => {
          updatePageField(data.id, data.fieldName, data.value, false);
        });
        broadcast.receive(`removePageField:${currentAdminId}`, (data) => {
          removePageField(data.id, false);
        });
        broadcast.receive(`toggleEnablePageField:${currentAdminId}`, (data) => {
          toggleEnablePageField(data.fieldName, data.value, false);
        });
        broadcast.receive(`toggleEnableField:${currentAdminId}`, (data) => {
          toggleEnableField(data.sliceId, data.fieldName, data.value, false);
        });
        broadcast.receive(`settingsUpdate:${currentAdminId}`, (data) => {
          updateElementSettings(data.elementId, data.settings, false);
        });
        broadcast.receive(`updateProperties:${currentAdminId}`, (data) => {
          updateProperties(data.elementId, data.properties, false);
        });
        broadcast.receive(`updateLayout:${currentAdminId}`, (data) => {
          updateLayout(data.layout, false);
        });
        broadcast.receive(`setIsSaving:${currentAdminId}`, (data) => {
          setIsSaving(data.value, false);
        });
        broadcast.receive(`setCurrentState:${currentAdminId}`, (data) => {
          setCurrentState(data.stateName, data.value, false);
        });
      } else if (inStudio) {
        const currentAdminId = voixStudioStore.currentAdminId;
        broadcast.receive(`awaitingCurrentPage:${currentAdminId}`, (data) => {
          sendCurrentPageToIframes();
        });
        broadcast.receive(`setCurrentState:${currentAdminId}`, (data) => {
          if (data.stateName === "currentlySelectedElements") {
            const state = useState(data.stateName, () => []);
            state.value = data.value;
          }
        });
      }
    }
  }
  function setupForAdministration(page) {
    if (page) {
      for (const element of page.elements)
        page.elements[page.elements.indexOf(element)] = addAdministationToElement(element);
    }
    return page;
  }
  function addAdministationToElement(element) {
    element.administration = {
      gui: { hydrationOptions: [], el: null },
      permissions: []
    };
    if (element.elements) {
      for (const e of element.elements)
        element.elements[element.elements.indexOf(e)] = addAdministationToElement(e);
    }
    return element;
  }
  function getGroupById(id) {
    let theGroup = null;
    if (currentPage.value) {
      for (const element of currentPage.value.elements) {
        if (element.type === "group" && element.id?.toString() === id.toString()) {
          theGroup = element;
        }
      }
    }
    return theGroup;
  }
  function updateSliceModelQuery(element, modelQuery) {
    if (currentPage.value && element) {
      if (modelQuery) {
        element.model_query = modelQuery;
        element.type = "query";
      } else {
        element.type = "slice";
        delete element.model_query;
      }
    }
  }
  function recursivelyFindElement(elementId, parentElement) {
    if (parentElement.elements) {
      for (const element of parentElement.elements) {
        if (element?.id?.toString() === elementId.toString())
          return { element, parentElement, index: parentElement.elements.indexOf(element) };
        if (element.elements) {
          const result = recursivelyFindElement(elementId, element);
          if (result)
            return result;
        }
      }
    }
    return void 0;
  }
  function findElement(elementId) {
    if (currentPage.value)
      return recursivelyFindElement(elementId, currentPage.value);
    return void 0;
  }
  function toggleEnableField(sliceId, fieldName, value, shouldBroadcast = true) {
    if (currentPage.value) {
      const foundElement = findElement(sliceId);
      if (foundElement) {
        const element = foundElement.element;
        if (element && element.type === "slice") {
          const slice = element;
          if (slice.fields) {
            const field = slice.fields.find((f) => f.name === fieldName);
            if (field && field.type) {
              field.enabled = value;
              if (shouldBroadcast)
                shareBroadcast("toggleEnableField", { sliceId, fieldName, value });
            }
          }
        }
      } else {
        console.error("Could not find element", sliceId);
      }
    }
  }
  function enableField(sliceId, fieldName) {
    toggleEnableField(sliceId, fieldName, true);
  }
  function disableField(sliceId, fieldName) {
    toggleEnableField(sliceId, fieldName, false);
  }
  function addElement(element, destinationId, shouldBroadcast = true) {
    if (currentPage.value && element) {
      const finalDestination = destinationId ? findElement(destinationId)?.element.elements : currentPage.value.elements;
      if (finalDestination) {
        finalDestination.push(element);
        if (shouldBroadcast)
          shareBroadcast("addElement", { element, destinationId });
      }
    }
  }
  function duplicateElement(elementId, newElement = null, shouldBroadcast = true) {
    if (currentPage.value && elementId) {
      const foundElement = findElement(elementId);
      const finalDestination = foundElement?.parentElement ? foundElement.parentElement : currentPage.value;
      if (foundElement && finalDestination && finalDestination.elements) {
        if (!newElement) {
          newElement = JSON.parse(JSON.stringify(foundElement.element));
          resetAllIds(newElement);
        }
        finalDestination.elements.splice(foundElement.index + 1, 0, newElement);
        if (shouldBroadcast)
          shareBroadcast("duplicateElement", { elementId, newElement });
      } else {
        console.warn("Could not find element or could not identify the elements parent. Did you just delete it?", elementId, foundElement);
      }
    }
  }
  function toggleElement(elementId, enabled) {
    if (enabled)
      enableElement(elementId);
    else disableElement(elementId);
  }
  function enableElement(elementId, shouldBroadcast = true) {
    if (currentPage.value && elementId) {
      const foundElement = findElement(elementId);
      if (foundElement && foundElement.parentElement?.elements) {
        foundElement.element.enabled = true;
        if (shouldBroadcast)
          shareBroadcast("enableElement", { elementId });
      } else {
        console.warn("Could not find element or could not identify the elements parent. Did you just delete it?", elementId, foundElement);
      }
    }
  }
  function disableElement(elementId, shouldBroadcast = true) {
    if (currentPage.value && elementId) {
      const foundElement = findElement(elementId);
      if (foundElement && foundElement.parentElement?.elements) {
        foundElement.element.enabled = false;
        if (shouldBroadcast)
          shareBroadcast("disableElement", { elementId });
      } else {
        console.warn("Could not find element or could not identify the elements parent. Did you just delete it?", elementId, foundElement);
      }
    }
  }
  function deleteElement(elementId, shouldBroadcast = true) {
    if (currentPage.value && elementId) {
      const foundElement = findElement(elementId);
      if (foundElement && foundElement.parentElement?.elements) {
        foundElement.parentElement.elements.splice(
          foundElement.parentElement.elements.indexOf(foundElement.element),
          1
        );
        if (shouldBroadcast)
          shareBroadcast("deleteElement", { elementId });
      } else {
        console.warn("Could not find element or could not identify the elements parent. Did you just delete it?", elementId, foundElement);
      }
    }
  }
  function moveElement(elementId, destinationId, shouldBroadcast = true) {
    const destinationIsRoot = destinationId === null;
    const foundElement = findElement(elementId);
    if (foundElement && currentPage.value) {
      const splicedItem = foundElement.parentElement.elements.splice(
        foundElement.parentElement.elements.indexOf(foundElement.element),
        1
      );
      if (destinationIsRoot) {
        currentPage.value.elements.unshift(splicedItem[0]);
      } else {
        const foundDestinationElement = findElement(destinationId);
        if (foundDestinationElement && foundDestinationElement.element.elements) {
          foundDestinationElement.parentElement.elements.splice(
            foundDestinationElement.parentElement.elements.indexOf(foundDestinationElement.element) + 1,
            0,
            splicedItem[0]
          );
        }
      }
      if (shouldBroadcast)
        shareBroadcast("moveElement", { elementId, destinationId });
    }
  }
  function setSlot(elementId, slot, shouldBroadcast = true) {
    const foundElement = findElement(elementId);
    if (foundElement && slot) {
      foundElement.element.slot = slot;
      if (shouldBroadcast)
        shareBroadcast("setSlot", { elementId, slot });
    }
  }
  function moveElementIntoElement(elementId, destinationId, shouldBroadcast = true) {
    const foundElement = findElement(elementId);
    const foundDestinationElement = findElement(destinationId);
    if (foundElement && foundDestinationElement && foundElement.element.id !== foundDestinationElement.element.id && currentPage.value) {
      const splicedItem = foundElement.parentElement.elements.splice(
        foundElement.parentElement.elements.indexOf(foundElement.element),
        1
      );
      if (foundDestinationElement && foundDestinationElement.element.elements) {
        foundDestinationElement.element.elements.splice(
          foundDestinationElement.element.elements.indexOf(foundDestinationElement.element) + 1,
          0,
          splicedItem[0]
        );
      }
      if (shouldBroadcast)
        shareBroadcast("moveElementIntoElement", { elementId, destinationId });
    }
  }
  function addXPTagToElement(sliceId, tag) {
    if (currentPage.value) {
      const foundElement = findElement(sliceId);
      if (foundElement?.element && !foundElement?.element?.tags) {
        foundElement.element.tags = [];
      }
      if (foundElement?.element?.tags)
        foundElement.element.tags.push(tag);
      else
        console.error("Could not find element", sliceId);
    }
  }
  function removeXPTagFromElement(sliceId, tag) {
    if (currentPage.value) {
      const foundElement = findElement(sliceId);
      if (foundElement?.element?.tags) {
        const index = foundElement.element.tags.findIndex((f) => f === tag);
        if (index > -1)
          foundElement.element.tags.splice(index, 1);
      } else {
        console.error("Could not find element", sliceId);
      }
    }
  }
  function addConditionToElement(sliceId, condition) {
    if (currentPage.value) {
      const foundElement = findElement(sliceId);
      if (foundElement?.element && !foundElement?.element?.conditions_settings) {
        foundElement.element.conditions_settings = {
          type: "and",
          conditions: []
        };
      }
      if (foundElement?.element?.conditions_settings)
        foundElement.element.conditions_settings.conditions.push(condition);
      else
        console.error("Could not find element", sliceId);
    }
  }
  function removeConditionFromElement(sliceId, condition) {
    if (currentPage.value) {
      const foundElement = findElement(sliceId);
      if (foundElement?.element?.conditions_settings) {
        const index = foundElement.element.conditions_settings.conditions.findIndex((f) => f.key === condition.key && f.operator === condition.operator && f.value === condition.value);
        if (index > -1)
          foundElement.element.conditions_settings.conditions.splice(index, 1);
      } else {
        console.error("Could not find element", sliceId);
      }
    }
  }
  function updateField(sliceId, fieldName, value, shouldBroadcast = true) {
    if (currentPage.value) {
      const foundElement = findElement(sliceId);
      if (foundElement) {
        const element = foundElement.element;
        if (element && element.type === "slice") {
          const slice = element;
          if (slice.fields) {
            const field = slice.fields.find((f) => f.name === fieldName);
            if (field && field.type) {
              field[field.type] = value;
              if (shouldBroadcast)
                shareBroadcast("fieldUpdate", { sliceId, fieldName, value });
            }
          }
        }
      } else {
        console.error("Could not find element", sliceId);
      }
    }
  }
  function addPageField(value, shouldBroadcast = true) {
    if (currentPage.value) {
      if (!currentPage.value.fields)
        currentPage.value.fields = [];
      const fieldIndex = currentPage.value.fields.push(value);
      if (shouldBroadcast)
        shareBroadcast("updatePageField", { value });
      return currentPage.value.fields[fieldIndex - 1];
    }
  }
  function updatePageField(id, fieldName, value, shouldBroadcast = true) {
    if (currentPage.value) {
      if (!currentPage.value.fields)
        currentPage.value.fields = [];
      let foundField = currentPage.value.fields.find((f) => f.id === id);
      if (!foundField) {
        value.name = fieldName;
        currentPage.value.fields.push(value);
        foundField = currentPage.value.fields.find((f) => f.name === fieldName);
      } else {
        value.name = fieldName;
        currentPage.value.fields[currentPage.value.fields.indexOf(foundField)] = value;
      }
      if (shouldBroadcast)
        shareBroadcast("updatePageField", { id, fieldName, value });
    }
  }
  function toggleEnablePageField(fieldName, value, shouldBroadcast = true) {
    if (currentPage.value?.fields) {
      const foundField = currentPage.value.fields.find((f) => f.name === fieldName);
      if (foundField) {
        currentPage.value.fields[currentPage.value.fields.indexOf(foundField)].enabled = value;
        if (shouldBroadcast)
          shareBroadcast("toggleEnablePageField", { fieldName, value });
      }
    }
  }
  function enablePageField(fieldName) {
    toggleEnablePageField(fieldName, true);
  }
  function disablePageField(fieldName) {
    toggleEnablePageField(fieldName, false);
  }
  function removePageField(id, shouldBroadcast = true) {
    if (currentPage.value?.fields) {
      const index = currentPage.value.fields.findIndex((f) => f.id === id);
      if (index > -1)
        currentPage.value.fields.splice(index, 1);
    }
    if (shouldBroadcast)
      shareBroadcast("removePageField", { id });
  }
  function updateElementSettings(elementId, settings, shouldBroadcast = true) {
    if (currentPage.value) {
      const foundElement = findElement(elementId);
      if (foundElement) {
        foundElement.element.properties.settings = settings;
        if (shouldBroadcast)
          shareBroadcast("settingsUpdate", { elementId, settings });
      } else {
        console.error("Could not find element", elementId);
      }
    }
  }
  function updateProperties(elementId, properties, shouldBroadcast = true) {
    if (currentPage.value) {
      const foundElement = findElement(elementId);
      if (foundElement) {
        foundElement.element.properties = properties;
        if (shouldBroadcast)
          shareBroadcast("updateProperties", { elementId, properties });
      } else {
        console.error("Could not find element", elementId);
      }
    }
  }
  function updateLayout(layout, shouldBroadcast = true) {
    if (currentPage.value) {
      currentPage.value.layout = layout;
      if (shouldBroadcast)
        shareBroadcast("updateLayout", { layout });
    }
  }
  function setPageResponse(responseData) {
    pageResponse.value = responseData;
  }
  function getParentElement(element) {
    const foundElement = findElement(element.id);
    if (foundElement)
      return foundElement.parentElement;
    return null;
  }
  function removeXPTag(tag, shouldBroadcast = true) {
    if (currentPage.value?.tags) {
      const index = currentPage.value.tags.findIndex((f) => f === tag);
      if (index > -1)
        currentPage.value.tags.splice(index, 1);
    }
    if (shouldBroadcast)
      shareBroadcast("removeXPTag", { tag });
  }
  const currentPageIsDirty = computed(() => {
    return JSON.stringify(currentPage.value) !== JSON.stringify(currentPageClean.value);
  });
  return {
    currentPath,
    currentDomain,
    currentPage,
    currentPageIsDirty,
    pageResponse,
    isSaving,
    inStudioIframe,
    setCurrentPage,
    setPageResponse,
    setIsSaving,
    addElement,
    getParentElement,
    unsetCurrentPage,
    checkAndSetupElements,
    setupForAdministration,
    addAdministationToElement,
    getGroupById,
    updateSliceModelQuery,
    toggleEnableField,
    enableField,
    disableField,
    duplicateElement,
    toggleElement,
    enableElement,
    disableElement,
    deleteElement,
    moveElement,
    setSlot,
    moveElementIntoElement,
    updateField,
    addPageField,
    updatePageField,
    toggleEnablePageField,
    enablePageField,
    disablePageField,
    removePageField,
    updateElementSettings,
    updateProperties,
    updateLayout,
    findElement,
    removeXPTag,
    addXPTagToElement,
    removeXPTagFromElement,
    addConditionToElement,
    removeConditionFromElement,
    setCurrentState,
    shareBroadcast,
    setUpBroadcastReceiversIfNecessary
  };
});
